import { Fragment, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageShell } from '@/components/Layout';
import { Device } from '@/lib/device';
import { useToast } from '@/lib/ui';
import { useAuth } from '@/hooks/useAuth';
import { useConfirmedMutation } from '@/api/mutations';
import {
  fetchUpdateData, fetchUpdateDataFacets, fetchUpdateDataRow,
  processUpdWeewxFn, purgeUpdWeewxFn,
} from '@/api/endpoints';
import type { UpdateDataListQuery, UpdateDataRow } from '@/api/types';
import { tableLabel, statusLabel, statusBadge, typeLabel, fmtLocal, STATUS_OPTIONS } from '@/lib/updateData';

const PAGE_SIZE = 50;

// Default purge cutoff = today − 5 days. new Date() is fine here: this is a fresh
// user-facing default in browser-local time, not a stored tz-less value being re-rendered.
function defaultBeforeDate(): string {
  const d = new Date();
  d.setDate(d.getDate() - 5);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00`;
}

export default function UpdWeewx({ device }: { device: Device }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superAdmin';
  const toast = useToast();
  const qc = useQueryClient();

  const [status, setStatus] = useState<string>('');      // '' = tous
  const [dateOp, setDateOp] = useState<'eq' | 'lt' | 'gt'>('lt');
  const [date, setDate] = useState<string>('');
  const [posteId, setPosteId] = useState<string>('');    // '' = toutes (value = poste_id)
  const [table, setTable] = useState<string>('');
  const [type, setType] = useState<string>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<UpdateDataRow | null>(null);
  const [showPurge, setShowPurge] = useState(false);

  const query: UpdateDataListQuery = {
    status: status !== '' ? Number(status) : undefined,
    date_op: date ? dateOp : undefined,
    date: date || undefined,
    poste_id: posteId !== '' ? Number(posteId) : undefined,
    table_name: table !== '' ? Number(table) : undefined,
    update_type: (type as 'U' | 'D') || undefined,
    q: search.trim() || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    sort: 'update_date_local',
    order: 'desc',
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['upd-weewx', query],
    queryFn: () => fetchUpdateData(query),
  });
  const { data: facets } = useQuery({ queryKey: ['upd-weewx', 'facets'], queryFn: fetchUpdateDataFacets });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // walk back a page if the current one emptied (e.g. after a purge)
  useEffect(() => {
    if (!isLoading && rows.length === 0 && page > 0) setPage((p) => p - 1);
  }, [isLoading, rows.length, page]);

  const tableOptions = useMemo(
    () => (facets?.tables ?? []).map((c) => ({ value: c, label: tableLabel(c) })).sort((a, b) => a.label.localeCompare(b.label)),
    [facets],
  );
  const selectedMeteor = posteId ? facets?.postes.find((p) => String(p.poste_id) === posteId)?.meteor : undefined;

  const refresh = () => qc.invalidateQueries({ queryKey: ['upd-weewx'] });
  const resetPage = () => setPage(0);

  const processMut = useConfirmedMutation(
    (_: void) => processUpdWeewxFn(selectedMeteor),
    {
      title: 'Traiter les mises à jour WeeWX',
      message: selectedMeteor
        ? `Planifier le traitement des mises à jour pour ${selectedMeteor} ?`
        : 'Planifier le traitement des mises à jour pour TOUTES les stations ?',
      onSuccess: () => { toast.push('Traitement planifié', 'success'); refresh(); },
      onError: (e) => { if (e.message !== 'ConfirmRejected') toast.push('Échec du traitement', 'error'); },
    },
  );

  const purgeMut = useConfirmedMutation(
    (input: { meteor?: string; before_date: string }) => purgeUpdWeewxFn(input),
    {
      title: 'Purger le journal update_data',
      message: 'Supprime les lignes déjà appliquées (Done) antérieures à la date choisie. Action irréversible.',
      destructive: true,
      onSuccess: (r) => { toast.push(`${r.supprimees_total} ligne(s) purgée(s)`, 'success'); setShowPurge(false); refresh(); },
      onError: (e) => { if (e.message !== 'ConfirmRejected') toast.push('Échec de la purge', 'error'); },
    },
  );

  const busy = processMut.isPending || purgeMut.isPending;

  return (
    <PageShell device={device} title="Upd WeeWX" breadcrumb="Services">
      <div className="p-4 md:p-6 space-y-4">
        {/* filter bar */}
        <div className="flex flex-wrap gap-2 items-center">
          <select value={status} onChange={(e) => { setStatus(e.target.value); resetPage(); }} className="h-9 px-2 rounded-md border border-input bg-background text-sm">
            <option value="">status — tous</option>
            {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>

          <div className="flex h-9 rounded-md border border-input bg-background overflow-hidden">
            <select value={dateOp} onChange={(e) => { setDateOp(e.target.value as 'eq' | 'lt' | 'gt'); resetPage(); }} className="px-2 bg-background text-sm border-r border-border" title="Opérateur de date" aria-label="Opérateur de date">
              <option value="eq">= (jour)</option>
              <option value="lt">&lt; avant</option>
              <option value="gt">&gt; après</option>
            </select>
            <input type="datetime-local" step={1} value={date} onChange={(e) => { setDate(e.target.value); resetPage(); }} className="px-2 bg-background text-sm" title="Date (heure locale station)" />
            {date && <button onClick={() => { setDate(''); resetPage(); }} className="px-2 text-muted-foreground hover:text-foreground" title="Effacer la date">✕</button>}
          </div>

          <select value={posteId} onChange={(e) => { setPosteId(e.target.value); resetPage(); }} className="h-9 px-2 rounded-md border border-input bg-background text-sm max-w-[160px]">
            <option value="">meteor — toutes</option>
            {(facets?.postes ?? []).map((p) => <option key={p.poste_id} value={p.poste_id}>{p.meteor}</option>)}
          </select>

          <select value={table} onChange={(e) => { setTable(e.target.value); resetPage(); }} className="h-9 px-2 rounded-md border border-input bg-background text-sm max-w-[200px]">
            <option value="">table — toutes</option>
            {tableOptions.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>

          <select value={type} onChange={(e) => { setType(e.target.value); resetPage(); }} className="h-9 px-2 rounded-md border border-input bg-background text-sm">
            <option value="">type — tous</option>
            <option value="U">Update</option>
            <option value="D">Delete</option>
          </select>

          <input
            type="search"
            placeholder="Rechercher meteor…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); resetPage(); }}
            className="h-9 px-3 rounded-md border border-input bg-background text-sm flex-1 min-w-[140px] max-w-xs"
          />

          <span className="text-xs text-muted-foreground ml-auto">
            {isLoading ? 'Chargement…' : `${total} ligne(s)`}
          </span>

          <button
            onClick={() => processMut.mutate()}
            disabled={busy}
            title={selectedMeteor ? `Traiter ${selectedMeteor}` : 'Traiter toutes les stations'}
            className="h-9 px-3 rounded-md text-sm font-medium border border-primary text-primary disabled:opacity-40"
          >
            Traiter{selectedMeteor ? ` · ${selectedMeteor}` : ''}
          </button>
          <button
            onClick={() => setShowPurge(true)}
            disabled={!isSuperAdmin || busy}
            title={isSuperAdmin ? 'Purger les lignes appliquées' : 'Réservé au superAdmin'}
            className="h-9 px-3 rounded-md text-sm font-medium border border-destructive text-destructive disabled:opacity-40"
          >
            Purger {isSuperAdmin ? '' : '🔒'}
          </button>
        </div>

        {isError ? (
          <div className="p-8 text-center">
            <p className="text-destructive font-medium">Erreur de chargement</p>
            <p className="text-sm text-muted-foreground mt-1">{error instanceof Error ? error.message : 'Erreur inconnue'}</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2.5 w-24">id</th>
                  <th className="text-left px-4 py-2.5">meteor</th>
                  <th className="text-left px-4 py-2.5">table</th>
                  <th className="text-left px-4 py-2.5 w-20">type</th>
                  <th className="text-left px-4 py-2.5">date locale</th>
                  <th className="text-left px-4 py-2.5 w-24">status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r) => (
                  <tr key={`${r.poste_id}-${r.id}`} className="cursor-pointer hover:bg-accent/30 select-none" onClick={() => setSelected(r)} title="Cliquer pour voir avant / après">
                    <td className="px-4 py-2.5 font-mono text-xs">{r.id}</td>
                    <td className="px-4 py-2.5 text-xs font-mono">{r.meteor}</td>
                    <td className="px-4 py-2.5 text-xs font-mono">{tableLabel(r.table_name)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`${r.update_type === 'D' ? 'badge-slate' : 'badge-blue'} px-1.5 py-0.5 rounded text-[10px] font-medium`}>{typeLabel(r.update_type)}</span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground font-mono whitespace-nowrap">{fmtLocal(r.update_date_local)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`${statusBadge(r.status)} px-1.5 py-0.5 rounded text-[10px] font-medium`}>{statusLabel(r.status)}</span>
                    </td>
                  </tr>
                ))}
                {!isLoading && rows.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">Aucune mise à jour dans le filtre.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-end gap-2 text-sm">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="h-8 px-3 rounded-md border border-border disabled:opacity-40">←</button>
            <span className="text-xs text-muted-foreground">page {page + 1} / {pages}</span>
            <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1} className="h-8 px-3 rounded-md border border-border disabled:opacity-40">→</button>
          </div>
        )}
      </div>

      {selected && <DetailSheet row={selected} onClose={() => setSelected(null)} />}
      {showPurge && (
        <PurgeSheet
          defaultMeteor={selectedMeteor}
          postes={facets?.postes ?? []}
          busy={purgeMut.isPending}
          onClose={() => setShowPurge(false)}
          onSubmit={(meteor, before) => purgeMut.mutate({ meteor, before_date: before })}
        />
      )}
    </PageShell>
  );
}

// ─── Detail sheet: diff of changed keys (default), toggle to full JSON ──────────
function valDisplay(v: unknown): string {
  if (v === undefined) return '—';
  if (v === null) return 'null';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

function DetailSheet({ row, onClose }: { row: UpdateDataRow; onClose: () => void }) {
  const [mode, setMode] = useState<'diff' | 'full'>('diff');
  const { data, isLoading, isError } = useQuery({
    queryKey: ['upd-weewx', row.poste_id, row.id],
    queryFn: () => fetchUpdateDataRow(row.poste_id, row.id),
  });

  const oldD = (data?.old_data ?? null) as Record<string, unknown> | null;
  const newD = (data?.new_data ?? null) as Record<string, unknown> | null;

  const changed = useMemo(() => {
    const keys = new Set([...Object.keys(oldD ?? {}), ...Object.keys(newD ?? {})]);
    return [...keys]
      .filter((k) => JSON.stringify(oldD?.[k]) !== JSON.stringify(newD?.[k]))
      .sort();
  }, [oldD, newD]);

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full md:w-[720px] bg-card border-l border-border shadow-2xl overflow-y-auto flex flex-col">
        <div className="sticky top-0 bg-background/95 backdrop-blur border-b border-border px-5 py-3 flex items-start justify-between z-10">
          <div>
            <div className="text-xs text-muted-foreground">
              <span className="font-mono">{row.meteor}</span> · id <span className="font-mono">{row.id}</span>
            </div>
            <h2 className="text-lg font-bold flex items-center gap-2 mt-0.5">
              <span className={`${row.update_type === 'D' ? 'badge-slate' : 'badge-blue'} px-1.5 py-0.5 rounded text-[10px] font-medium`}>{typeLabel(row.update_type)}</span>
              <span className="font-mono text-sm">{tableLabel(row.table_name)}</span>
              <span className={`${statusBadge(row.status)} px-1.5 py-0.5 rounded text-[10px] font-medium`}>{statusLabel(row.status)}</span>
            </h2>
            <div className="text-xs text-muted-foreground font-mono mt-0.5">{fmtLocal(row.update_date_local)}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-muted" aria-label="Fermer">✕</button>
        </div>

        <div className="px-5 pt-3">
          <div className="inline-flex h-8 rounded-md border border-input overflow-hidden text-xs font-medium">
            <button onClick={() => setMode('diff')} className={`px-3 ${mode === 'diff' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>Clés modifiées</button>
            <button onClick={() => setMode('full')} className={`px-3 border-l border-border ${mode === 'full' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>JSON complet</button>
          </div>
        </div>

        <div className="p-5 space-y-4 flex-1">
          {isLoading && <p className="text-sm text-muted-foreground">Chargement…</p>}
          {isError && <p className="text-sm text-destructive">Impossible de charger le détail.</p>}

          {!isLoading && !isError && mode === 'diff' && (
            <section>
              {changed.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucune clé modifiée.</p>
              ) : (
                <div className="border border-border rounded-md overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left px-3 py-2">clé</th>
                        <th className="text-left px-3 py-2">avant</th>
                        <th className="text-left px-3 py-2">après</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border font-mono">
                      {changed.map((k) => (
                        <tr key={k}>
                          <td className="px-3 py-1.5 font-semibold">{k}</td>
                          <td className="px-3 py-1.5 whitespace-pre-wrap break-all text-muted-foreground">{valDisplay(oldD?.[k])}</td>
                          <td className="px-3 py-1.5 whitespace-pre-wrap break-all">{newD ? valDisplay(newD?.[k]) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {!isLoading && !isError && mode === 'full' && (
            <div className="grid gap-4">
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">old_data</h3>
                <pre className="bg-muted/30 font-mono text-[11px] p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-all border border-border">{oldD ? JSON.stringify(oldD, null, 2) : 'null'}</pre>
              </section>
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">new_data</h3>
                <pre className="bg-muted/30 font-mono text-[11px] p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-all border border-border">{newD ? JSON.stringify(newD, null, 2) : 'null'}</pre>
              </section>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-background/95 backdrop-blur border-t border-border px-5 py-3 flex justify-end">
          <button onClick={onClose} className="h-9 px-4 rounded-md bg-secondary text-secondary-foreground text-sm font-medium">Fermer</button>
        </div>
      </aside>
    </div>
  );
}

// ─── Purge sheet: station (or all) + before-date cutoff ─────────────────────────
function PurgeSheet({
  defaultMeteor, postes, busy, onClose, onSubmit,
}: {
  defaultMeteor?: string;
  postes: { poste_id: number; meteor: string }[];
  busy: boolean;
  onClose: () => void;
  onSubmit: (meteor: string | undefined, before: string) => void;
}) {
  const [meteor, setMeteor] = useState<string>(defaultMeteor ?? '');
  const [before, setBefore] = useState<string>(defaultBeforeDate());

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full md:w-[460px] bg-card border-l border-border shadow-2xl overflow-y-auto flex flex-col">
        <div className="border-b border-border px-5 py-3 flex items-center justify-between">
          <h2 className="text-lg font-bold">Purger le journal</h2>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-muted" aria-label="Fermer">✕</button>
        </div>
        <form
          className="p-5 space-y-4 flex-1"
          onSubmit={(e) => { e.preventDefault(); onSubmit(meteor || undefined, before); }}
        >
          <p className="text-sm text-muted-foreground">
            Supprime les lignes déjà appliquées (status <span className="font-mono">Done</span>) dont la date locale est <em>antérieure</em> à la date choisie. Les lignes en attente ou en erreur ne sont jamais purgées.
          </p>
          <label className="block text-sm">
            Station
            <select value={meteor} onChange={(e) => setMeteor(e.target.value)} className="mt-1 w-full h-9 px-2 rounded-md border border-input bg-background text-sm">
              <option value="">Toutes les stations</option>
              {postes.map((p) => <option key={p.poste_id} value={p.meteor}>{p.meteor}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            Purger avant (date locale)
            <input type="datetime-local" step={1} required value={before} onChange={(e) => setBefore(e.target.value)} className="mt-1 w-full h-9 px-2 rounded-md border border-input bg-background text-sm" />
          </label>
          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onClose} className="h-9 px-4 rounded-md bg-secondary text-secondary-foreground text-sm font-medium">Annuler</button>
            <button type="submit" disabled={busy} className="h-9 px-4 rounded-md text-sm font-medium border border-destructive text-destructive disabled:opacity-40">
              {busy ? 'Purge…' : 'Purger'}
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}
